import {
  useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Ellipsis, Search, X, type LucideIcon } from "lucide-react";
import { cx, IconButton, Kbd, useIsPhone } from "./ui.js";

/*
 * Floating layers. Every dialog, sheet, popover and the task panel registers in one
 * stack so Escape and outside clicks only ever close the top-most layer.
 */
type LayerKind = "dialog" | "popover" | "panel";
interface Layer { id: number; kind: LayerKind; el: () => HTMLElement | null; anchor?: () => HTMLElement | null; close: () => void }
const stack: Layer[] = [];
let nextId = 1;
let installed = false;

function install() {
  if (installed || typeof document === "undefined") return;
  installed = true;
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || !stack.length) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    stack[stack.length - 1]!.close();
  }, true);
  document.addEventListener("pointerdown", (e) => {
    const top = stack[stack.length - 1];
    if (!top || top.kind !== "popover") return;
    const t = e.target as Node;
    if (top.el()?.contains(t) || top.anchor?.()?.contains(t)) return;
    top.close();
  }, true);
}

/** True when any dialog, popover or panel is open (global shortcuts stay quiet then). */
export const overlayOpen = (kinds: LayerKind[] = ["dialog", "popover"]) => stack.some((l) => kinds.includes(l.kind));

export function useLayer(kind: LayerKind, el: () => HTMLElement | null, close: () => void, anchor?: () => HTMLElement | null) {
  const closeRef = useRef(close);
  closeRef.current = close;
  useEffect(() => {
    install();
    const layer: Layer = { id: nextId++, kind, el, anchor, close: () => closeRef.current() };
    stack.push(layer);
    return () => { const i = stack.indexOf(layer); if (i >= 0) stack.splice(i, 1); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

// ------------------------------------------------------------------ helpers
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function useFocusTrap(ref: React.RefObject<HTMLElement | null>, active = true, restore = true) {
  useEffect(() => {
    if (!active) return;
    const previous = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => {
      const el = ref.current;
      if (!el || el.contains(document.activeElement)) return;
      // Prefer an explicit autofocus target; otherwise focus the layer itself so no control shows a focus ring on open.
      const auto = el.querySelector<HTMLElement>("[data-autofocus], [autofocus]");
      (auto ?? el).focus({ preventScroll: true });
    });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !ref.current) return;
      const nodes = Array.from(ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.offsetParent !== null || n === document.activeElement);
      if (!nodes.length) return;
      const first = nodes[0]!;
      const last = nodes[nodes.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    const el = ref.current;
    el?.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      el?.removeEventListener("keydown", onKey);
      if (restore && previous?.isConnected) previous.focus({ preventScroll: true });
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps
}

let locks = 0;
function useScrollLock(active = true) {
  useEffect(() => {
    if (!active) return;
    const main = document.getElementById("main-scroll");
    if (locks++ === 0) {
      document.body.style.overflow = "hidden";
      if (main) main.style.overflow = "hidden";
    }
    return () => {
      if (--locks === 0) {
        document.body.style.overflow = "";
        if (main) main.style.overflow = "";
      }
    };
  }, [active]);
}

/** How far the on-screen keyboard covers the layout viewport (phones). */
export function useKeyboardInset(): { inset: number; height: number | null } {
  const [state, setState] = useState<{ inset: number; height: number | null }>({ inset: 0, height: null });
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => setState({ inset: Math.max(0, window.innerHeight - vv.height - vv.offsetTop), height: vv.height });
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => { vv.removeEventListener("resize", update); vv.removeEventListener("scroll", update); };
  }, []);
  return state;
}

export function Portal({ children }: { children: ReactNode }) {
  if (typeof document === "undefined") return null;
  return createPortal(children, document.body);
}

// ------------------------------------------------------------------ dialog / sheet
export interface DialogProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** sm 400 · md 480 · lg 640 · xl 760 */
  size?: "sm" | "md" | "lg" | "xl";
  description?: ReactNode;
  /** Replace the default header (title + close) */
  header?: ReactNode;
  hideTitle?: boolean;
  headerActions?: ReactNode;
  /** On phone: "sheet" hugs content, "full" fills the screen */
  phone?: "sheet" | "full";
  bodyClassName?: string;
  /** Desktop placement: centred, or near the top like a command bar */
  placement?: "center" | "top";
  /** Extra content pinned under the body on phone (e.g. a toolbar above the keyboard) */
  className?: string;
}

/** Modal dialog on desktop; bottom sheet with grab handle and swipe-down on phone. */
export function Dialog({ title, onClose, children, footer, size = "md", description, header, hideTitle, headerActions, phone = "sheet", bodyClassName, placement = "center", className }: DialogProps) {
  const isPhone = useIsPhone();
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const [drag, setDrag] = useState(0);
  const [leaving, setLeaving] = useState(false);
  const dragStart = useRef<{ y: number; t: number } | null>(null);
  const kb = useKeyboardInset();
  useLayer("dialog", () => panelRef.current, onClose);
  useFocusTrap(panelRef);
  useScrollLock();

  const dismissAnimated = useCallback(() => {
    setLeaving(true);
    setDrag(panelRef.current?.offsetHeight ?? 600);
    window.setTimeout(onClose, 200);
  }, [onClose]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!isPhone || (e.target as HTMLElement).closest("button, input, textarea, a")) return;
    dragStart.current = { y: e.clientY, t: Date.now() };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragStart.current) return;
    setDrag(Math.max(0, e.clientY - dragStart.current.y));
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (!dragStart.current) return;
    const dy = e.clientY - dragStart.current.y;
    const v = dy / Math.max(1, Date.now() - dragStart.current.t);
    dragStart.current = null;
    if (dy > 110 || (dy > 30 && v > 0.6)) dismissAnimated();
    else setDrag(0);
  };

  const widths = { sm: "desk:max-w-[400px]", md: "desk:max-w-[480px]", lg: "desk:max-w-[640px]", xl: "desk:max-w-[760px]" };
  const sheetStyle: CSSProperties | undefined = isPhone ? {
    transform: drag ? `translateY(${drag}px)` : undefined,
    transition: dragStart.current ? "none" : "transform 200ms var(--ease-out)",
    bottom: kb.inset || undefined,
    maxHeight: kb.height ? kb.height - 12 : undefined,
    height: phone === "full" ? (kb.height ? kb.height - 12 : "calc(100dvh - 12px - env(safe-area-inset-top))") : undefined,
  } : undefined;

  return (
    <Portal>
      <div className={cx("fixed inset-0 z-50 bg-backdrop anim-fade", leaving && "opacity-0 transition-opacity duration-200")} style={isPhone && drag && !leaving ? { opacity: Math.max(0.2, 1 - drag / 400) } : undefined}
        onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="dialog-backdrop" />
      <div className={cx("pointer-events-none fixed inset-0 z-50 flex justify-center", isPhone ? "items-end" : placement === "top" ? "items-start pt-[12vh]" : "items-center p-6")}>
        <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} style={sheetStyle}
          className={cx("pointer-events-auto flex w-full flex-col overflow-hidden bg-surface-raised text-text outline-none",
            isPhone ? "max-h-[calc(100dvh-12px-env(safe-area-inset-top))] rounded-t-sheet shadow-modal anim-sheet" : cx("max-h-[85dvh] rounded-sheet border border-border shadow-modal anim-dialog", widths[size]),
            className)}>
          <div onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} className={cx(isPhone && "touch-none")}>
            {isPhone && <div className="flex justify-center pb-1 pt-2" aria-hidden><span className="h-1 w-9 rounded-full bg-border-strong" /></div>}
            {header ?? (
              <header className={cx("flex items-center gap-2 px-5 phone:px-4", hideTitle ? "pt-3" : "border-b border-border py-3", isPhone && "pt-1")}>
                <div className={cx("min-w-0 flex-1", hideTitle && "sr-only")}>
                  <h2 id={titleId} className="truncate text-heading font-semibold">{title}</h2>
                  {description && <p className="text-meta text-text-muted">{description}</p>}
                </div>
                {headerActions}
                <IconButton icon={X} label="Close" shortcut={isPhone ? undefined : "Esc"} onClick={isPhone ? dismissAnimated : onClose} />
              </header>
            )}
            {header && <h2 id={titleId} className="sr-only">{title}</h2>}
          </div>
          <div className={cx("min-h-0 flex-1 overflow-y-auto overscroll-contain", bodyClassName ?? "px-5 py-4 phone:px-4")}>{children}</div>
          {footer && <footer className="flex items-center justify-end gap-2 border-t border-border px-5 py-3 phone:px-4 phone:pb-[max(12px,env(safe-area-inset-bottom))]">{footer}</footer>}
          {!footer && isPhone && <div className="pb-safe" />}
        </div>
      </div>
    </Portal>
  );
}

// ------------------------------------------------------------------ popover
export function usePopover<T extends HTMLElement = HTMLElement>() {
  const [anchor, setAnchor] = useState<T | null>(null);
  return {
    anchor,
    isOpen: !!anchor,
    open: (el: T | null) => setAnchor(el),
    toggle: (e: { currentTarget: EventTarget }) => { const el = e.currentTarget as T; setAnchor((a) => (a ? null : el)); },
    close: () => setAnchor(null),
  };
}

export interface PopoverProps {
  anchor: HTMLElement | null;
  onClose: () => void;
  children: ReactNode;
  /** Used as the sheet title on phone and the accessible name */
  title: string;
  align?: "start" | "end" | "center";
  width?: number;
  className?: string;
  /** Prefer to open beside the anchor (sidebar rail) */
  side?: "bottom" | "right";
  /** Keep a popover (not a sheet) on phone, e.g. tiny tooltips */
  noSheet?: boolean;
}

/** Anchored floating panel on desktop; bottom sheet on phone. */
export function Popover(props: PopoverProps) {
  const isPhone = useIsPhone();
  if (isPhone && !props.noSheet) {
    return <Dialog title={props.title} onClose={props.onClose} bodyClassName="pb-2" size="sm">{props.children}</Dialog>;
  }
  return <FloatingPanel {...props} />;
}

function FloatingPanel({ anchor, onClose, children, title, align = "start", width = 280, className, side = "bottom" }: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayer("popover", () => ref.current, onClose, () => anchor);
  useFocusTrap(ref, true, true);

  const place = useCallback(() => {
    const el = ref.current;
    if (!el || !anchor) return;
    const r = anchor.getBoundingClientRect();
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top: number;
    let left: number;
    if (side === "right") {
      left = r.right + 8;
      top = Math.min(r.top, vh - h - 8);
    } else {
      top = r.bottom + 6;
      if (top + h > vh - 8 && r.top - h - 6 > 8) top = r.top - h - 6;
      left = align === "end" ? r.right - w : align === "center" ? r.left + r.width / 2 - w / 2 : r.left;
    }
    left = Math.max(8, Math.min(left, vw - w - 8));
    top = Math.max(8, Math.min(top, vh - h - 8));
    setPos({ top, left });
  }, [anchor, align, side]);

  useLayoutEffect(() => { place(); }, [place]);
  useEffect(() => {
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(place) : null;
    if (ref.current) ro?.observe(ref.current);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { ro?.disconnect(); window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [place]);

  return (
    <Portal>
      <div ref={ref} role="dialog" aria-label={title} tabIndex={-1}
        style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, width, visibility: pos ? "visible" : "hidden" }}
        className={cx("fixed z-[60] max-h-[min(600px,calc(100dvh-16px))] overflow-y-auto overscroll-contain rounded-card border border-border bg-surface-raised text-text shadow-pop outline-none anim-pop", className)}>
        {children}
      </div>
    </Portal>
  );
}

// ------------------------------------------------------------------ option lists
export interface Option {
  key: string;
  label: string;
  icon?: LucideIcon;
  iconColor?: string;
  dot?: string;
  /** Text on the right, e.g. the resolved weekday or a shortcut */
  hint?: ReactNode;
  selected?: boolean;
  indent?: number;
  danger?: boolean;
  disabled?: boolean;
  /** Draw a divider above this row */
  divider?: boolean;
  /** Small heading above this row */
  group?: string;
  leading?: ReactNode;
  onSelect: () => void;
}

/** Keyboard-navigable list of choices with optional search. role=listbox or role=menu. */
export function OptionList({ options, search, placeholder = "Search", emptyText = "No matches", role = "listbox", multi, label, footer, onCreate }: {
  options: Option[]; search?: boolean; placeholder?: string; emptyText?: string; role?: "listbox" | "menu"; multi?: boolean; label: string; footer?: ReactNode;
  /** Shown as the last row when the search text matches nothing exactly */
  onCreate?: (text: string) => { label: string; run: () => void } | null;
}) {
  const [q, setQ] = useState("");
  const [active, setActive] = useState(() => Math.max(0, options.findIndex((o) => o.selected)));
  const listId = useId();
  const listRef = useRef<HTMLDivElement>(null);
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
    const create = needle && onCreate ? onCreate(q.trim()) : null;
    return create ? [...list, { key: "__create", label: create.label, onSelect: create.run, icon: undefined } as Option] : list;
  }, [q, options, onCreate]);
  useEffect(() => { if (q) setActive(0); }, [q]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [active]);

  const choose = (o: Option | undefined) => { if (o && !o.disabled) o.onSelect(); };
  const onKey = (e: ReactKeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(filtered.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (e.key === "Home") { e.preventDefault(); setActive(0); }
    else if (e.key === "End") { e.preventDefault(); setActive(filtered.length - 1); }
    else if (e.key === "Enter" || (e.key === " " && !search)) { e.preventDefault(); choose(filtered[active]); }
  };
  const itemRole = role === "menu" ? (multi ? "menuitemcheckbox" : "menuitem") : "option";

  return (
    <div onKeyDown={onKey}>
      {search && (
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-surface-raised px-3">
          <Search size={15} className="text-text-faint" aria-hidden />
          <input data-autofocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} aria-label={placeholder}
            role="combobox" aria-expanded aria-controls={listId} aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
            className="h-10 min-w-0 flex-1 bg-transparent text-body outline-none phone:h-12" />
        </div>
      )}
      <div ref={listRef} id={listId} role={role} aria-label={label} aria-multiselectable={role === "listbox" && multi ? true : undefined}
        tabIndex={search ? -1 : 0} data-autofocus={search ? undefined : true}
        aria-activedescendant={!search && filtered[active] ? `${listId}-${active}` : undefined}
        className="py-1 outline-none">
        {filtered.map((o, i) => {
          const Icon = o.icon;
          return (
            <div key={o.key}>
              {o.divider && <div className="my-1 h-px bg-border" role="separator" />}
              {o.group && <div className="px-3 pb-1 pt-2 text-caption font-semibold uppercase text-text-faint">{o.group}</div>}
              <div id={`${listId}-${i}`} data-index={i} role={itemRole} aria-selected={role === "listbox" ? !!o.selected : undefined}
                aria-checked={role === "menu" && multi ? !!o.selected : undefined} aria-disabled={o.disabled || undefined}
                onMouseMove={() => setActive(i)} onClick={() => choose(o)}
                className={cx("mx-1 flex min-h-8 cursor-pointer select-none items-center gap-2.5 rounded-control px-2 text-body phone:min-h-12 phone:px-3",
                  i === active && "bg-hover", o.danger ? "text-danger" : "text-text", o.disabled && "cursor-default opacity-40")}
                style={o.indent ? { paddingLeft: 8 + o.indent * 14 } : undefined}>
                {o.leading}
                {o.dot && <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: o.dot }} />}
                {Icon && <Icon size={16} aria-hidden className={o.danger ? "" : o.iconColor ? "" : "text-text-muted"} style={o.iconColor ? { color: o.iconColor } : undefined} />}
                <span className="min-w-0 flex-1 truncate">{o.label}</span>
                {o.hint && <span className="shrink-0 text-meta text-text-faint">{o.hint}</span>}
                {o.selected && <Check size={15} className="shrink-0 text-brand-text" aria-hidden />}
              </div>
            </div>
          );
        })}
        {!filtered.length && <div className="px-4 py-6 text-center text-body text-text-faint">{emptyText}</div>}
      </div>
      {footer}
    </div>
  );
}

// ------------------------------------------------------------------ menu
export interface MenuItem { label: string; onSelect: () => void; icon?: LucideIcon; danger?: boolean; hidden?: boolean; shortcut?: string; divider?: boolean; disabled?: boolean }

export function MenuList({ items, onClose, label }: { items: MenuItem[]; onClose: () => void; label: string }) {
  const visible = items.filter((i) => !i.hidden);
  return (
    <OptionList role="menu" label={label} options={visible.map((it, i) => ({
      key: `${i}-${it.label}`, label: it.label, icon: it.icon, danger: it.danger, divider: it.divider, disabled: it.disabled,
      hint: it.shortcut ? <Kbd>{it.shortcut}</Kbd> : undefined,
      onSelect: () => { onClose(); it.onSelect(); },
    }))} />
  );
}

/** "⋯" button with a menu (popover on desktop, sheet on phone). */
export function Menu({ items, label = "More actions", icon = Ellipsis, align = "end", size = "md", className, title }: {
  items: MenuItem[]; label?: string; icon?: LucideIcon; align?: "start" | "end"; size?: "sm" | "md"; className?: string; title?: string;
}) {
  const pop = usePopover<HTMLButtonElement>();
  return (
    <>
      <IconButton icon={icon} label={label} size={size} className={className} aria-haspopup="menu" aria-expanded={pop.isOpen} active={pop.isOpen}
        onClick={(e) => { e.stopPropagation(); pop.toggle(e); }} />
      {pop.anchor && (
        <Popover anchor={pop.anchor} onClose={pop.close} title={title ?? label} align={align} width={232}>
          <MenuList items={items} onClose={pop.close} label={label} />
        </Popover>
      )}
    </>
  );
}

// ------------------------------------------------------------------ select
export interface SelectOption<T> { value: T; label: string; icon?: LucideIcon; iconColor?: string; dot?: string; hint?: ReactNode; indent?: number }

/** Button that opens a list of options. Replaces native <select> everywhere. */
export function Select<T extends string | number | null>({ value, onChange, options, label, placeholder = "Choose…", className, search, width = 240, variant = "field", icon }: {
  value: T; onChange: (v: T) => void; options: SelectOption<T>[]; label: string; placeholder?: string; className?: string; search?: boolean; width?: number;
  variant?: "field" | "chip" | "ghost"; icon?: LucideIcon;
}) {
  const pop = usePopover<HTMLButtonElement>();
  const current = options.find((o) => o.value === value);
  const Icon = current?.icon ?? icon;
  return (
    <>
      <button type="button" aria-label={`${label}: ${current?.label ?? placeholder}`} aria-haspopup="listbox" aria-expanded={pop.isOpen} onClick={pop.toggle}
        className={cx("inline-flex min-w-0 items-center gap-2 text-left text-body transition-colors duration-150",
          variant === "field" && "h-9 w-full rounded-control border border-border bg-surface-raised px-3 hover:border-border-strong phone:h-11",
          variant === "chip" && "h-8 rounded-full border border-border bg-surface-raised px-3 text-meta font-medium text-text-muted hover:border-border-strong hover:text-text phone:h-10",
          variant === "ghost" && "h-8 rounded-control px-2 text-text-muted hover:bg-hover hover:text-text phone:h-11",
          pop.isOpen && variant === "field" && "border-brand ring-2 ring-brand/20", className)}>
        {current?.dot && <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: current.dot }} />}
        {Icon && <Icon size={15} aria-hidden className="shrink-0" style={current?.iconColor ? { color: current.iconColor } : undefined} />}
        <span className={cx("min-w-0 flex-1 truncate", !current && "text-text-faint")}>{current?.label ?? placeholder}</span>
        <ChevronDown size={14} aria-hidden className="shrink-0 text-text-faint" />
      </button>
      {pop.anchor && (
        <Popover anchor={pop.anchor} onClose={pop.close} title={label} width={Math.max(width, pop.anchor.offsetWidth)}>
          <OptionList label={label} search={search} options={options.map((o, i) => ({
            key: String(i), label: o.label, icon: o.icon, iconColor: o.iconColor, dot: o.dot, hint: o.hint, indent: o.indent, selected: o.value === value,
            onSelect: () => { onChange(o.value); pop.close(); },
          }))} />
        </Popover>
      )}
    </>
  );
}

// ------------------------------------------------------------------ tooltips
/**
 * One tooltip for the whole app. Any element with data-tip="Text" (and optionally
 * data-tip-key="Q" and data-tip-side="right") gets a tooltip after 300ms of hover or
 * keyboard focus. Touch input never shows tooltips.
 */
export function TooltipLayer() {
  const [tip, setTip] = useState<{ text: string; key?: string; rect: DOMRect; side: string } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    let timer: number | undefined;
    let current: HTMLElement | null = null;
    const hide = () => { window.clearTimeout(timer); current = null; setTip(null); setPos(null); };
    const show = (el: HTMLElement) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (!el.isConnected || current !== el || !el.dataset.tip) return;
        setTip({ text: el.dataset.tip, key: el.dataset.tipKey, rect: el.getBoundingClientRect(), side: el.dataset.tipSide ?? "bottom" });
      }, 300);
    };
    const over = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      const el = (e.target as Element | null)?.closest?.<HTMLElement>("[data-tip]") ?? null;
      if (el === current) return;
      hide();
      if (el?.dataset.tip) { current = el; show(el); }
    };
    const touchOnly = () => !!window.matchMedia?.("(hover: none)")?.matches;
    const focusIn = (e: FocusEvent) => {
      if (touchOnly()) return;
      const el = (e.target as Element | null)?.closest?.<HTMLElement>("[data-tip]") ?? null;
      let visible = false;
      try { visible = !!el?.matches(":focus-visible"); } catch { visible = false; }
      if (el?.dataset.tip && visible) { current = el; show(el); }
    };
    document.addEventListener("pointerover", over);
    document.addEventListener("focusin", focusIn);
    document.addEventListener("focusout", hide);
    document.addEventListener("pointerdown", hide, true);
    document.addEventListener("keydown", hide, true);
    window.addEventListener("scroll", hide, true);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("pointerover", over);
      document.removeEventListener("focusin", focusIn);
      document.removeEventListener("focusout", hide);
      document.removeEventListener("pointerdown", hide, true);
      document.removeEventListener("keydown", hide, true);
      window.removeEventListener("scroll", hide, true);
    };
  }, []);

  useLayoutEffect(() => {
    if (!tip || !ref.current) return;
    const w = ref.current.offsetWidth;
    const h = ref.current.offsetHeight;
    const r = tip.rect;
    let top: number;
    let left: number;
    if (tip.side === "right") { left = r.right + 8; top = r.top + r.height / 2 - h / 2; }
    else {
      top = r.bottom + 6;
      if (top + h > window.innerHeight - 4) top = r.top - h - 6;
      left = r.left + r.width / 2 - w / 2;
    }
    setPos({ top, left: Math.max(6, Math.min(left, window.innerWidth - w - 6)) });
  }, [tip]);

  if (!tip) return null;
  return (
    <Portal>
      <div ref={ref} role="tooltip" style={{ top: pos?.top ?? -999, left: pos?.left ?? -999, visibility: pos ? "visible" : "hidden" }}
        className="pointer-events-none fixed z-[100] flex items-center gap-1.5 rounded-control bg-text px-2 py-1 text-meta font-medium text-surface shadow-pop anim-fade">
        {tip.text}
        {tip.key && <span className="rounded-[4px] bg-surface/20 px-1 text-caption tracking-normal">{tip.key}</span>}
      </div>
    </Portal>
  );
}

// ------------------------------------------------------------------ confirm / prompt
interface ConfirmRequest { title: string; body?: ReactNode; confirmLabel?: string; danger?: boolean; input?: { label: string; value: string }; resolve: (v: string | boolean | null) => void }
let confirmState: ConfirmRequest | null = null;
const confirmListeners = new Set<() => void>();
const setConfirm = (v: ConfirmRequest | null) => { confirmState = v; confirmListeners.forEach((l) => l()); };

/** Asks the user to confirm. Resolves true or false. */
export function confirmDialog(opts: { title: string; body?: ReactNode; confirmLabel?: string; danger?: boolean }): Promise<boolean> {
  return new Promise((resolve) => setConfirm({ ...opts, resolve: (v) => resolve(v === true) }));
}
/** Asks for a short text value (e.g. a new name). Resolves null when cancelled. */
export function promptDialog(opts: { title: string; label: string; value?: string; confirmLabel?: string }): Promise<string | null> {
  return new Promise((resolve) => setConfirm({ title: opts.title, confirmLabel: opts.confirmLabel ?? "Save", input: { label: opts.label, value: opts.value ?? "" }, resolve: (v) => resolve(typeof v === "string" ? v : null) }));
}

/** Renders confirmDialog / promptDialog requests. Mounted once in the app shell. */
export function ConfirmHost() {
  const [, force] = useState(0);
  useEffect(() => { const l = () => force((n) => n + 1); confirmListeners.add(l); return () => { confirmListeners.delete(l); }; }, []);
  const req = confirmState;
  const [value, setValue] = useState("");
  useEffect(() => { setValue(req?.input?.value ?? ""); }, [req]);
  if (!req) return null;
  const done = (v: string | boolean | null) => { setConfirm(null); req.resolve(v); };
  const ok = () => done(req.input ? (value.trim() || null) : true);
  return (
    <Dialog title={req.title} size="sm" onClose={() => done(req.input ? null : false)}
      footer={<>
        <button type="button" onClick={() => done(req.input ? null : false)} className="h-8 rounded-control px-3 text-body font-medium text-text-muted hover:bg-hover hover:text-text phone:h-11">Cancel</button>
        <button type="button" onClick={ok} disabled={!!req.input && !value.trim()}
          className={cx("h-8 rounded-control px-3 text-body font-medium text-white disabled:opacity-50 phone:h-11", req.danger ? "bg-danger dark:text-black" : "bg-brand-strong")}>{req.confirmLabel ?? "OK"}</button>
      </>}>
      {req.body && <div className="text-body text-text-muted">{req.body}</div>}
      {req.input && (
        <label className="block">
          <span className="mb-1.5 block text-meta font-medium text-text-muted">{req.input.label}</span>
          <input data-autofocus value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); ok(); } }}
            className="h-9 w-full rounded-control border border-border bg-surface-raised px-3 text-body outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 phone:h-11" />
        </label>
      )}
    </Dialog>
  );
}
